import React from "react";
import dayjs from "utils/dayjs";
import { titleCase } from "title-case";
import { SummaryItem } from "components/common/Summary";
import { Stack } from "components/layout/reusableLayouts";

export type EventLogActor = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  apiKeyName?: string | null;
};

export type EventLogService =
  | "IMPERSONATION"
  | "ACCOUNTS"
  | "USERS"
  | "PLATFORM"
  | "REWARDS"
  | "API_KEYS"
  | "WEBHOOKS";

export type EventLogNode = {
  id: string;
  eventType: string;
  service: EventLogService;
  dataType: string | null;
  occurredAt: number | null;
  actor: EventLogActor | null;
  eventData: string;
};

// Row enriched with `parsed` (eventData parsed once) and `label` (humanized
// event type computed once). Used to avoid re-parsing JSON / re-deriving the
// label inside cell renderers on every render.
export type EventLogRow = EventLogNode & {
  parsed: Record<string, unknown>;
  label: string;
};

type EventLogEdge = { node: EventLogNode };
export type PlatformEventLogsQueryData = {
  platformEventLogs?: { edges?: EventLogEdge[] };
};

const HUMAN_EVENT_LABEL: Record<string, string> = {
  IMPERSONATION_STARTED: "Started impersonation session",
  IMPERSONATION_ACTION: "Impersonation action",
  PLATFORM_OWNER_ADD_ACCOUNT: "Added account",
  PLATFORM_OWNER_UPDATE_ACCOUNT: "Updated account",
  PLATFORM_OWNER_REQUEST_DEACTIVATE_ACCOUNT: "Requested account deactivation",
  PLATFORM_OWNER_ADD_USER: "Added user",
  PLATFORM_OWNER_UPDATE_USER: "Updated user",
  COMPANY_PLANS_UPDATED: "Updated account plans",
  PLATFORM_BRANDING_UPDATED: "Updated platform branding",
  PLATFORM_EMBEDDED_ORIGINS_UPDATED: "Updated embedded origins",
  REWARD_RULE_CREATED: "Created earn rule",
  REWARD_RULE_UPDATED: "Updated earn rule",
  REWARD_RULE_PAUSED: "Paused earn rule",
  REWARD_RULE_RESUMED: "Resumed earn rule",
  REWARDS_FUNDING_BANK_ADDED: "Added rewards funding bank",
  REWARDS_FUNDING_BANK_REMOVED: "Removed rewards funding bank",
  REWARDS_POOL_FUNDING_REQUESTED: "Requested rewards pool funding",
};

const HUMAN_MUTATION_LABEL: Record<string, string> = {
  endImpersonationSession: "Ended impersonation session",
};

const HUMAN_FIELD_LABEL: Record<string, string> = {
  companies: "Accounts",
};

// v2 audit rows name a resource and an action rather than a type; a person's
// webhook endpoint change logs as WEBHOOK_ENDPOINT_ACTION.
const ACTOR_ACTION_EVENT_TYPES = new Set([
  "API_KEY_ACTION",
  "USER_ACTION",
  "WEBHOOK_ENDPOINT_ACTION",
]);

const HUMAN_RESOURCE_ACTION_LABEL: Record<string, Record<string, string>> = {
  webhookEndpoint: {
    create: "Created webhook endpoint",
    update: "Updated webhook endpoint",
    delete: "Deleted webhook endpoint",
    rotateSecret: "Rotated webhook signing secret",
    test: "Sent test webhook",
    replayEvents: "Replayed webhook events",
  },
};

const SERVICE_LABEL_KEY: Record<EventLogService, string> = {
  IMPERSONATION: "Impersonation",
  ACCOUNTS: "Accounts",
  USERS: "Users",
  PLATFORM: "Platform",
  REWARDS: "Rewards",
  API_KEYS: "API keys",
  WEBHOOKS: "Webhooks",
};

export const serviceLabelKey = (service: EventLogService): string =>
  SERVICE_LABEL_KEY[service];

export const formatActor = (actor: EventLogActor | null): string => {
  if (!actor) return "System";
  if (actor.apiKeyName) return `${actor.apiKeyName} (API key)`;
  const name = [actor.firstName, actor.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name && actor.email) return `${name} (${actor.email})`;
  return name || actor.email || actor.id;
};

export const safeParse = (json: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

export const humanizeEventType = (
  eventType: string,
  parsed: Record<string, unknown>,
): string => {
  if (eventType === "IMPERSONATION_ACTION") {
    const mut = parsed.mutationName as string | undefined;
    if (mut && HUMAN_MUTATION_LABEL[mut]) return HUMAN_MUTATION_LABEL[mut];
    if (mut) return mut;
  }
  if (ACTOR_ACTION_EVENT_TYPES.has(eventType)) {
    const label = humanizeResourceAction(parsed);
    if (label) return label;
  }
  return HUMAN_EVENT_LABEL[eventType] || sentenceCaseCode(eventType);
};

const humanizeResourceAction = (
  parsed: Record<string, unknown>,
): string | null => {
  const { resourceType, action } = parsed;
  if (typeof resourceType !== "string" || typeof action !== "string") {
    return null;
  }
  return (
    HUMAN_RESOURCE_ACTION_LABEL[resourceType]?.[action] ??
    sentenceCaseCode(`${resourceType} ${action}`)
  );
};

// An unmapped code reads as a sentence, not a SHOUTED_TOKEN or camelCase run.
export const sentenceCaseCode = (code: string): string => {
  const words = code
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/_/g, " ")
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// Pre-parses eventData + humanizes label once per row, at extract time, so
// cell renderers and the drawer can read them without redoing the work on
// every render.
export const extractEventLogRows = (
  data: PlatformEventLogsQueryData,
): EventLogRow[] =>
  data?.platformEventLogs?.edges?.map((edge) => {
    const parsed = safeParse(edge.node.eventData);
    return {
      ...edge.node,
      parsed,
      label: humanizeEventType(edge.node.eventType, parsed),
    };
  }) ?? [];

const personLine = (name: unknown, email: unknown): string | null => {
  const n = typeof name === "string" ? name : "";
  const e = typeof email === "string" ? email : "";
  if (n && e) return `${n} (${e})`;
  return n || e || null;
};

const formatWhen = (occurredAt: number | null): React.ReactNode => {
  if (!occurredAt) return null;
  return <span>{dayjs(occurredAt).format("MMM D, YYYY h:mm A")}</span>;
};

const text = (value: string | null | undefined): React.ReactNode =>
  value ? <span>{value}</span> : null;

export function buildImpersonationItems(
  parsed: Record<string, unknown>,
  occurredAt: number | null,
  actor: EventLogActor | null,
  actionLabel: string,
  t: (key: string) => string,
): SummaryItem[] {
  const target = personLine(parsed.targetUserName, parsed.targetUserEmail);
  return [
    {
      title: t("Date/Time"),
      value: formatWhen(occurredAt),
    },
    {
      title: t("Action"),
      value: text(actionLabel),
    },
    {
      title: t("Performed by"),
      value: text(formatActor(actor)),
      fullWidth: true,
    },
    {
      title: t("Viewing as"),
      value: text(target),
      fullWidth: true,
    },
    {
      title: t("Account"),
      value: text(parsed.targetCompanyName as string | null | undefined),
    },
    {
      title: t("Platform"),
      value: text(parsed.targetPlatformName as string | null | undefined),
    },
  ];
}

const isPlainList = (value: unknown): value is (string | number)[] =>
  Array.isArray(value) &&
  value.every((item) => typeof item === "string" || typeof item === "number");

// The baseline reset strips list markers, which a list of plans needs to read as one.
const listSx = {
  margin: 0,
  paddingInlineStart: "var(--space-6)",
  "& > li": { listStyleType: "disc" },
} as const;

const detailValue = (
  value: unknown,
  t: (key: string) => string,
): React.ReactNode => {
  if (value === null || value === undefined) return null;
  if (isPlainList(value)) {
    if (value.length === 0) return text(t("None"));
    return (
      <Stack component="ul" gap="var(--space-1)" sx={listSx}>
        {value.map((item, index) => (
          <li key={`${index}-${item}`}>{item}</li>
        ))}
      </Stack>
    );
  }
  if (typeof value === "object") return text(JSON.stringify(value, null, 2));
  return text(String(value));
};

export function buildGenericItems(
  parsed: Record<string, unknown>,
  occurredAt: number | null,
  actor: EventLogActor | null,
  actionLabel: string,
  t: (key: string) => string,
): SummaryItem[] {
  const base: SummaryItem[] = [
    {
      title: t("Date/Time"),
      value: formatWhen(occurredAt),
    },
    {
      title: t("Action"),
      value: text(actionLabel),
    },
    {
      title: t("Performed by"),
      value: text(formatActor(actor)),
      fullWidth: true,
    },
  ];

  const friendly = Object.entries(parsed)
    .filter(([key]) => !key.toLowerCase().endsWith("id") && key !== "sessionId")
    .map(([key, value]) => ({
      title:
        HUMAN_FIELD_LABEL[key] ??
        titleCase(key.replace(/([A-Z])/g, " $1").trim()),
      value: detailValue(value, t),
      fullWidth: typeof value === "object" && value !== null,
    }));

  return [...base, ...friendly];
}
